/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { all, Promise } from 'rsvp';
import { isBlank, isEmpty, isNone } from '@ember/utils';
import { Task, task } from 'ember-concurrency';
import { serializeFilters } from 'cio/utils/conditions/serialize/event';
import { deserializeFilters as deserializeFormFilters } from 'cio/utils/conditions/deserialize/form';
import AuthService from 'cio/services/auth';

import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import { queryConcat, queryEncode } from 'cio/helpers/query';
import CampaignModel, { CampaignType } from 'cio/models/campaign';
import CustomerModel from 'cio/models/customer';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import LogModel from 'cio/models/log';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import DS from 'ember-data';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import CustomFormsService from 'cio/services/custom-forms';
import AnalyticsService from 'cio/services/analytics';
import { ComposerActionTypeName } from 'cio/utils/composer';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import ObjectModel from 'cio/models/object';
import ObjectTypeModel from 'cio/models/object-type';
import { ManualTrigger } from 'cio/components/action-composer';

export type ContextViewerTab = 'eventData' | 'sampleData' | 'attributes';
export type ContextViewerFilter = 'attribute' | 'email';

interface ContextViewerArgs {
  editableEntity: ComposerEditableEntity;
  actionTypeName: ComposerActionTypeName;
  selectedCustomer?: CustomerModel;

  onSelectCustomer(customer?: CustomerModel): unknown;

  onSelectEvent(event?: LogModel): unknown;

  onSelectTrigger(trigger?: BroadcastTriggerModel | ManualTrigger): unknown;

  onInitialMergeDataLoadFinished(): unknown;

  showHideButton?: boolean;

  onHide(): unknown;

  allowUnfocusedLiquidInsertion?: boolean;

  relatedObjects: ObjectModel[];
  relationshipAttributeMap: Record<string, Record<string, any>>;

  selectedTrigger?: BroadcastTriggerModel | ManualTrigger;
  onSelectObject(object: ObjectModel): unknown;

  hasReadOnlyPermission: boolean;
}

enum SampleDataTabs {
  Attributes = 'attributes',
  EventData = 'eventData',
  SampleData = 'sampleData',
  ObjectRelationships = 'objectRelationships',
  Trigger = 'trigger',
}

export default class ContextViewer extends Component<ContextViewerArgs> {
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;
  @service store!: DS.Store;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;

  @tracked hasInitialMergeData = false;
  @tracked isLiquidFieldFocused = false;
  @tracked initialMergeData: any;
  @tracked selectedTab = 'attributes';
  @tracked triggers: BroadcastTriggerModel[] = [];
  @tracked mergeData = null;
  @tracked hasMergeData = true;
  @tracked isLoadingCustomers = false;
  @tracked selectedEvent?: LogModel;
  @tracked activeFilter: ContextViewerFilter = 'email';
  @tracked attributeCondition: any = null;
  @tracked isAnonymousEventCampaign = false;
  @tracked selectedObjectTypeIndex = 0;
  @tracked selectedObjectIndex = 0;
  @tracked searchTerm = '';

  sampleDataTabs = SampleDataTabs;

  eventsTask = task(async (eventsOrEventsPromise: any) => {
    let events = await eventsOrEventsPromise;

    return events;
  });

  get events() {
    return this.eventsTask.last?.value;
  }

  @action
  onLiquidFocusIn() {
    this.isLiquidFieldFocused = true;
  }

  @action
  onLiquidFocusOut() {
    this.isLiquidFieldFocused = false;
  }

  get isLiquidInsertionDisabled() {
    if (this.args.allowUnfocusedLiquidInsertion) {
      return false;
    }

    return !this.isLiquidFieldFocused;
  }

  get liquidInsertAnalyticsData() {
    return {
      campaign: this.args.editableEntity.campaign,
      actionTypeName: this.args.actionTypeName,
      selectedTab: this.selectedTab,
    };
  }

  get attributes() {
    let { selectedCustomer } = this.args;

    if (isNone(selectedCustomer)) {
      return [];
    }

    let attributes = selectedCustomer.attrs;

    if (isNone(attributes)) {
      return [];
    }

    let searchTermLower = this.searchTerm.toLowerCase();

    return Object.keys(attributes).flatMap((key) => {
      if (searchTermLower !== '' && key.toLowerCase().includes(searchTermLower) === false) {
        return [];
      }
      return {
        label: key,
        value: attributes[key],
      };
    });
  }

  get otherAttributes() {
    let attributes = this.attributes.map((attr) => attr.label);
    let searchTermLower = this.searchTerm.toLowerCase();

    return this.environment.sentAttributes.reject(
      (attr) =>
        attributes.includes(attr) || (searchTermLower !== '' && attr.toLowerCase().includes(searchTermLower) === false),
    );
  }

  get filteredAttributeCount() {
    return this.attributes.length + this.otherAttributes.length;
  }

  get showHideButton() {
    return this.args.showHideButton ?? true;
  }

  get showLiquidFallbackModal() {
    return this.auth.user?.userFlags.show_liquid_fallback_modal ?? true;
  }

  constructor(owner: unknown, args: ContextViewerArgs) {
    super(owner, args);

    if (this.args.editableEntity.isCampaign && this.args.editableEntity.campaign?.isFormTriggered) {
      this.facebookLeadAds.initialize();
      this.customForms.initialize();
    }
  }

  @action
  onInsert() {
    this.loadMergeData.perform();

    let selectedTab;

    if (this.args.editableEntity.isCampaign && this.args.editableEntity.campaign?.isEventTriggered) {
      selectedTab = 'eventData';
    } else if (
      (this.args.editableEntity.isCampaign &&
        ((this.args.editableEntity.campaign?.isApiTriggered ?? false) ||
          (this.args.editableEntity.campaign?.isWebhookTriggered ?? false))) ||
      this.args.editableEntity.isTransactionalMessage
    ) {
      selectedTab = 'sampleData';
    } else {
      selectedTab = 'attributes';
    }

    this.selectedTab = selectedTab;
    this.attributeCondition = { operator: '=', type: 'attribute' };
  }

  loadMergeData = task(async () => {
    let loader: Task<unknown, [ComposerEditableEntity]> | null = null;
    if (this.args.editableEntity.isCampaign) {
      loader = {
        [CampaignType.Behavioral]: this.loadSegmentCampaignData,
        [CampaignType.Transactional]: this.loadEventCampaignData,
        [CampaignType.Form]: this.loadEventCampaignData,
        [CampaignType.APITriggered]: this.loadApiTriggeredCampaignData,
        [CampaignType.Date]: this.loadDateTriggeredCampaignData,
        [CampaignType.Webhook]: this.loadWebhookCampaignData,
        [CampaignType.Object]: this.loadObjectCampaignData,
        [CampaignType.ObjectRelationship]: this.loadRelationshipCampaignData,
        [CampaignType.None]: null,
      }[(this.args.editableEntity.campaign as CampaignModel).type];
    } else if (this.args.editableEntity.isNewsletter) {
      loader = this.loadSegmentCampaignData;
    } else if (this.args.editableEntity.isTransactionalMessage) {
      loader = this.loadTransactionalMessageData;
    }

    if (!loader) {
      throw new Error(`Unexpected campaign type ${this.args.editableEntity.model.id}`);
    }

    await loader.perform(this.args.editableEntity);
    this.args.onInitialMergeDataLoadFinished();
    return true;
  });

  loadTransactionalMessageData = task(async () => {
    await this.globalCustomerFallback.perform();
  });

  loadEventCampaignData = task(async (editableEntity: ComposerEditableEntity) => {
    let eventLogsPromise = this.loadEventLogs(editableEntity.campaign as CampaignModel);
    this.apiErrors.attach(eventLogsPromise);

    let result = await eventLogsPromise;
    let { anonymousEvents, customers } = result;

    let isAnonymousEventCampaign = !isEmpty(anonymousEvents) && isEmpty(customers);
    let isRegularEventCampaign = !isEmpty(customers) && isEmpty(anonymousEvents);

    if (isEmpty(anonymousEvents) && isEmpty(customers)) {
      // The event logs came back as empty.
      //
      // At this point, there is no way to know if this could be an anonymous
      // event campaign, so assume that it is not and fallback to loading
      // customers.
      await this.globalCustomerFallback.perform();
    } else {
      if (isAnonymousEventCampaign) {
        this.eventsTask.perform(anonymousEvents);
        this.isAnonymousEventCampaign = true;
        this.hasInitialMergeData = true;

        this._selectEvent(anonymousEvents[0]);
      } else if (isRegularEventCampaign) {
        this.setComponentPropertiesForCustomers(customers);
      } else {
        // TODO: If we got here, the integration is broken, somehow there is a
        // mix of anonymous events and regular events. We should alert the
        // customer and try to help them fix it.
        //
        // For now, let's assume the anonymous events were a mistake.
        this.setComponentPropertiesForCustomers(customers);
      }
    }
  });

  loadWebhookCampaignData = task(async (editableEntity: ComposerEditableEntity) => {
    let campaign = editableEntity.campaign as CampaignModel;
    try {
      let promise = campaign.fetchWebhookEventLogs();
      this.hasInitialMergeData = true;

      await this.eventsTask.perform(promise).then(() => {
        if (this.events.length > 0) {
          this._selectEvent(this.events[0]);
        }
      });
    } catch (e) {
      this.apiErrors.report(e);
    }
  });

  loadSegmentCampaignData = task(async (editableEntity: ComposerEditableEntity) => {
    let filters;

    if (editableEntity.isCampaign) {
      filters = queryConcat(editableEntity.campaign?.anchors, editableEntity.campaign?.filters);
    } else {
      let campaignFilters = editableEntity.newsletter?.filters;
      if (!isNone(campaignFilters)) {
        filters = queryEncode(campaignFilters);
      }
    }

    let filteredCustomersPromise = this.loadCustomers({ filters });
    this.apiErrors.attach(filteredCustomersPromise);
    let filteredCustomers = await filteredCustomersPromise;

    if (!isEmpty(filteredCustomers)) {
      this.setComponentPropertiesForCustomers(filteredCustomers.toArray());
    } else {
      // No customers matched the newsletter or campaign.
      //
      // Fallback to just loading customers that don't match so they can at
      // least preview the message.
      await this.globalCustomerFallback.perform();
    }
  });

  loadDateTriggeredCampaignData = task(async (editableEntity: ComposerEditableEntity) => {
    let campaign = editableEntity.campaign as CampaignModel;
    let filteredCustomers = await this.loadCustomers({
      filters: queryEncode([{ field: campaign.dateTriggeredAttribute, operator: 'exists', type: 'attribute' }]),
    });

    if (!isEmpty(filteredCustomers)) {
      this.setComponentPropertiesForCustomers(filteredCustomers.toArray());
    } else {
      await this.globalCustomerFallback.perform();
    }
  });

  loadApiTriggeredCampaignData = task(async (editableEntity: ComposerEditableEntity) => {
    let campaign = editableEntity.campaign as CampaignModel;
    let triggers = await this.store.query('broadcast-trigger', { campaign_id: campaign.id });
    this.triggers = triggers.toArray();

    let filters = campaign.recipientsFilterQuery;
    let filteredCustomersPromise = this.loadCustomers({ filters });
    this.apiErrors.attach(filteredCustomersPromise);

    let filteredCustomers = await filteredCustomersPromise;

    if (!isEmpty(filteredCustomers)) {
      this.setComponentPropertiesForCustomers(filteredCustomers.toArray());
    } else {
      // No customers matched the newsletter or campaign.
      //
      // Fallback to just loading customers that don't match so they can at
      // least preview the message.
      await this.globalCustomerFallback.perform();
    }
  });

  loadObjectCampaignData = task(async (editableEntity: ComposerEditableEntity) => {
    let campaign = editableEntity.campaign as CampaignModel;
    let filters = queryConcat(campaign.anchors, campaign.filters);

    let filteredCustomersPromise = this.loadCustomers({ filters });
    this.apiErrors.attach(filteredCustomersPromise);
    let filteredCustomers = await filteredCustomersPromise;

    if (!isEmpty(filteredCustomers)) {
      this.setComponentPropertiesForCustomers(filteredCustomers.toArray());
    } else {
      await this.globalCustomerFallback.perform();
    }
  });

  loadRelationshipCampaignData = task(async (editableEntity: ComposerEditableEntity) => {
    let campaign = editableEntity.campaign as CampaignModel;
    let filters = queryConcat(campaign.anchors, campaign.filters);

    let filteredCustomersPromise = this.loadCustomers({ filters });
    this.apiErrors.attach(filteredCustomersPromise);
    let filteredCustomers = await filteredCustomersPromise;

    if (!isEmpty(filteredCustomers)) {
      this.setComponentPropertiesForCustomers(filteredCustomers.toArray());
    } else {
      await this.globalCustomerFallback.perform();
    }
  });

  globalCustomerFallback = task(async () => {
    // Load all customers: only called for event campaigns or segment campaigns with no customers
    let unfilteredCustomersPromise = this.loadCustomers();
    this.apiErrors.attach(unfilteredCustomersPromise);

    let unfilteredCustomers = await unfilteredCustomersPromise;
    this.setComponentPropertiesForCustomers(unfilteredCustomers.toArray());
  });

  @action
  selectEvent(event: LogModel) {
    this._selectEvent(event);
    this.analytics.track('message', 'sample_data_event_changed');
  }

  @action
  selectTrigger(trigger: BroadcastTriggerModel | ManualTrigger) {
    this.args.onSelectTrigger(trigger);
  }

  @action
  onSelectCustomerAction(customer: CustomerModel) {
    this.selectCustomer(customer);
  }

  @action
  selectTab(tabName: SampleDataTabs) {
    switch (tabName) {
      case SampleDataTabs.Attributes:
        this.analytics.track('message', 'sample_data_attributes_viewed');
        break;
      case SampleDataTabs.EventData:
        this.analytics.track('message', 'sample_data_events_viewed');
        break;
      case SampleDataTabs.ObjectRelationships:
        this.analytics.track('message', 'sample_data_relationships_viewed');
        break;
    }

    this.selectedTab = tabName;
  }

  @action
  setSearchTerm(term: string) {
    this.searchTerm = term.trim();
  }

  get objectsPerType() {
    return this.args.relatedObjects.reduce(
      (acc, object) => {
        let objectType = object.belongsTo('objectType').value() as ObjectTypeModel;
        if (objectType != null) {
          if (acc[objectType.name] == null) {
            acc[objectType.name] = [];
          }
          acc[objectType.name].push(object);
        }
        return acc;
      },
      {} as Record<string, ObjectModel[]>,
    );
  }

  get objectTypeOptions() {
    return Object.keys(this.objectsPerType)
      .sort()
      .map((name, index) => ({
        label: name,
        value: index,
      }));
  }

  get relatedObjectsForSelectedType() {
    return this.objectsPerType[this.objectTypeOptions[this.selectedObjectTypeIndex]?.label] ?? [];
  }

  get objectRelationshipCampaignObjectType() {
    return this.args.editableEntity.campaign?.objectTypeForCampaign;
  }

  private async selectCustomer(customer: CustomerModel | undefined) {
    await this.args.onSelectCustomer(customer);
    this.loadEventsFor(customer);

    // reset relationship index
    this.selectedObjectIndex = 0;
    // reset object type dropdown selection
    this.selectedObjectTypeIndex = 0;
    if (this.args.relatedObjects.length === 0 && this.selectedTab === SampleDataTabs.ObjectRelationships) {
      this.selectedTab = SampleDataTabs.Attributes;
    }
  }

  private _selectEvent(event: LogModel | undefined) {
    this.args.onSelectEvent(event);
    this.selectedEvent = event;
  }

  private loadEventLogs(campaign: CampaignModel) {
    let promise = new Promise<{ anonymousEvents: LogModel[]; customers: CustomerModel[] }>((resolve, reject) => {
      let query = {
        type: 'event',
        name: campaign.event,
        since: true,
      };

      return this.store
        .query('log', query)
        .then((logs) => {
          let anonymousEvents: LogModel[] = [];
          let customers: CustomerModel[] = [];

          let promises = logs.map((log) => {
            return new Promise((resolve) => {
              log
                .get('customer')
                .then((customer) => {
                  if (customer) {
                    customers.push(customer);
                  } else {
                    anonymousEvents.push(log);
                  }
                })
                .catch((reason) => {
                  if (reason.errors[0].status === '404') {
                    // A 404 means this is a log for a customer who has been
                    // deleted. We cannot send emails to customers who have
                    // been deleted so we do not want to show them as available
                    // merge data. Do nothing here.
                  } else {
                    // FIXME: Status code was something other than 404, retry!
                  }
                })
                .finally(resolve);
            });
          });

          all(promises).then(() => {
            resolve({
              anonymousEvents,
              customers: customers.uniq(),
            });
          });
        })
        .catch(reject);
    });

    return promise;
  }

  private loadEventsFor(customer: CustomerModel | undefined) {
    if (!(this.args.editableEntity.campaign instanceof CampaignModel)) {
      return;
    }

    let eventName = this.args.editableEntity.campaign.event;

    if (!isNone(customer) && !isBlank(eventName)) {
      let promise;
      if (!this.args.editableEntity.campaign.isFormTriggered) {
        promise = customer.queryLogs({
          type: 'event',
          name: eventName,
        });
      } else {
        let serialized = serializeFilters(
          deserializeFormFilters(this.args.editableEntity.campaign.attributeFilters),
          'or',
        );
        let filters = queryEncode(serialized);

        promise = customer.queryLogs({
          type: 'event',
          name: eventName,
          filters,
        });
      }

      this.eventsTask
        .perform(promise)
        .then((events) => {
          this._selectEvent(events[0] as LogModel);
        })
        .catch(ignorePromiseFailure);
    }
  }

  private setComponentPropertiesForCustomers(customers: CustomerModel[]) {
    if (!isEmpty(customers)) {
      this.hasInitialMergeData = true;
      this.initialMergeData = customers;
      this.selectCustomer(customers[0]);
    }
  }

  private loadCustomers(params = {}) {
    return this.store.query('customer', params);
  }
}
